// Global Instructions Rule Applied!
//
// Statement run form (spec A3.14 stage 5).
//
// A run is one quarter's investor statements: a name, a period end (a
// quarter end), the statement date its statements print, and one fund or
// many. The form has three sections:
//   Details     the run's own fields. Creating a run is an explicit Create
//               (the New Fund form is the pattern); afterwards each field
//               saves on its own (InputManager auto-patch). The period is
//               fixed while the run holds funds.
//   Funds       the funds of the run, where each stands, Generate, Mark as
//               issued, Download all.
//   Statements  the statements stored for them: View, Download, Generate
//               again (after issue: the next version, "Revised"), Out of
//               date, Download all, and Send statements (stage 6: a draft
//               email per investor account, reviewed and queued in the
//               compose form) with the Sent column.
//   Performance reviews   the investor performance deck of every account
//               of the run (a run-level document made with the fund
//               reports; docs/QUARTERLY_REPORT_DISTRIBUTION_DESIGN.md d.1,
//               PR 4): View, Download, Generate again, Delete, the Sent
//               column. Shown while an account-scope document type is
//               configured.
//
// Delete (the form header) trashes the run with its funds and stored
// statements: any user while nothing in it is issued, afterwards an
// application administrator.

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { Button, Col, Popconfirm, Row, Spin, Tag, Tooltip, Typography } from 'antd'
import dayjs from 'dayjs'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../../core/utils/errorToast.tsx'
import { summariseFieldErrors } from '../../../../core/utils/utilities.ts'
import { isRequiredFieldValid } from '../../../../core/components/InputManager/utils/formatters.ts'
import { FormSection } from '../../../../core/components/FormSection.tsx'
import { InputManager } from '../../../../core/components/InputManager/index.ts'
import { FieldConfig, FieldTypeEnum, SelectTypeEnum } from '../../../../core/components/InputManager/types.ts'
import { useTabManagerContext } from '../../../../core/app-layout/lib/TabManagerContext.tsx'
import { useRefreshOnActivate } from '../../../../core/hooks/useRefreshOnActivate.ts'
import { API_ROUTES } from '../../../../core/utils/endpoints.ts'
import { transformFormTab, updateTabTitle } from '../../../../store/slices/tabSlice'
import { store } from '../../../../state_manager/store.ts'
import { getAccessToken } from '../../../../core/auth/authService'
import { TABLE_INVESTOR_STATEMENTS } from '../../../../state_manager/globals.ts'
import { createInvestorStatementRun, deleteInvestorStatementRun } from '../../services/investorStatementsService.ts'
import { useInvestorStatementRun } from '../../hooks/useInvestorStatementRun.ts'
import { useRunFundChanges } from '../../hooks/useRunFundChanges.ts'
import { useRunDescriptionWarnings } from '../../hooks/useRunDescriptionWarnings.ts'
import type { InvestorStatementRun } from '../../../../state_manager/data_models/investor_statement_run.ts'
import { DATE_FORMAT, ISO_DATE, lastCompletedQuarterEnd, periodLabel, periodOptions } from '../../utils/runDisplay.ts'
import RunFundsSection from '../RunFundsSection.tsx'
import RunStatementsSection from '../RunStatementsSection.tsx'
import RunAccountDocumentsSection from '../RunAccountDocumentsSection.tsx'

const { Title } = Typography

// The run's fields, keyed table__column: InputManager PATCHes the column.
const NAME = 'investor_statement_runs__name'
const PERIOD_END = 'investor_statement_runs__period_end'
const STATEMENT_DATE = 'investor_statement_runs__statement_date'
const NOTES = 'investor_statement_runs__notes'

// InputManager builds `${backend}/${patchEndpoint}`: the route path without
// its leading slash.
const RUN_PATCH = API_ROUTES.INVESTOR_STATEMENTS.RUN_PATCH.path.replace(/^\//, '')

const PERIOD_HELP =
  'The quarter the statements are made for; they state the books at the close of its last day.'
const STATEMENT_DATE_HELP = 'The date printed on the statements of the run.'

interface RunValues {
  [NAME]: string | null
  [PERIOD_END]: string | null
  [STATEMENT_DATE]: string | null
  [NOTES]: string | null
}

/** The Details fields, for the create form and for the run's own. */
export function getRunFields(
  values: RunValues,
  options: { patchEndpoint?: string; periodLocked?: boolean; statementDateLocked?: boolean } = {}
): FieldConfig[] {
  const patch = options.patchEndpoint ? { patchEndpoint: options.patchEndpoint } : {}
  return [
    { key: NAME, label: 'Name', type: FieldTypeEnum.TEXT, value: values[NAME], editable: true, required: true, ...patch },
    {
      key: PERIOD_END,
      label: 'Period',
      type: FieldTypeEnum.SELECT,
      value: values[PERIOD_END],
      options: periodOptions(dayjs(), 12, values[PERIOD_END]),
      // newest quarter first
      optionOrder: 'given',
      selectType: SelectTypeEnum.FILTER,
      allowClear: false,
      editable: !options.periodLocked,
      disabled: !!options.periodLocked,
      required: true,
      labelTooltip: options.periodLocked
        ? `${PERIOD_HELP} It cannot change while the run holds funds: their statements are of this period.`
        : PERIOD_HELP,
      ...patch
    },
    {
      key: STATEMENT_DATE,
      label: 'Statement Date',
      type: FieldTypeEnum.DATE,
      value: values[STATEMENT_DATE],
      format: DATE_FORMAT,
      allowClear: false,
      editable: !options.statementDateLocked,
      disabled: !!options.statementDateLocked,
      required: true,
      labelTooltip: options.statementDateLocked
        ? `${STATEMENT_DATE_HELP} It cannot change once statements of the run are issued.`
        : `${STATEMENT_DATE_HELP} Statements already generated keep the date they were printed with until they are generated again.`,
      ...patch
    },
    {
      key: NOTES,
      label: 'Notes',
      type: FieldTypeEnum.TEXTAREA,
      value: values[NOTES],
      editable: true,
      rows: 2,
      autoSize: { minRows: 2, maxRows: 6 },
      ...patch
    }
  ]
}

interface FullInvestorStatementRunFormProps {
  recordId?: string | null
  isNew?: boolean
}

export default function FullInvestorStatementRunForm({
  recordId: propRecordId,
  isNew: propIsNew
}: FullInvestorStatementRunFormProps) {
  const { urlRecordId } = useParams()
  const recordId = propRecordId !== undefined ? propRecordId : urlRecordId ?? null
  const isNew = propIsNew !== undefined ? propIsNew : recordId === 'new' || !recordId
  const formTabKey = `${TABLE_INVESTOR_STATEMENTS}-form-${isNew ? 'new' : 'edit'}-${recordId || 'new'}`

  return isNew || !recordId ? (
    <NewRunForm formTabKey={formTabKey} />
  ) : (
    <RunForm runId={recordId} formTabKey={formTabKey} />
  )
}

// ---------------------------------------------------------------------------
// Create
// ---------------------------------------------------------------------------

function NewRunForm({ formTabKey }: { formTabKey: string }) {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { closeFormTab } = useTabManagerContext()

  const [draft, setDraft] = useState<RunValues>(() => {
    const period = lastCompletedQuarterEnd().format(ISO_DATE)
    return {
      [NAME]: `${periodLabel(period)} Statements`,
      [PERIOD_END]: period,
      [STATEMENT_DATE]: dayjs().format(ISO_DATE),
      [NOTES]: null
    }
  })
  // the name follows the period until the user writes their own
  const [nameTouched, setNameTouched] = useState(false)
  const [dirty, setDirty] = useState(false)
  const [saving, setSaving] = useState(false)
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({})
  const [validationErrorsVersion, setValidationErrorsVersion] = useState(0)

  useEffect(() => {
    dispatch(updateTabTitle({ key: formTabKey, title: 'New Statement Run' }))
  }, [dispatch, formTabKey])

  const fields = useMemo(() => getRunFields(draft), [draft])

  const handleChange = useCallback(
    (key: string, value: unknown) => {
      setDraft((prev) => {
        const next = { ...prev, [key]: (value as string | null) ?? null }
        if (key === PERIOD_END && !nameTouched && typeof value === 'string' && value) {
          next[NAME] = `${periodLabel(value)} Statements`
        }
        return next
      })
      if (key === NAME) setNameTouched(true)
      setDirty(true)
      setValidationErrors((prev) => {
        if (!(key in prev)) return prev
        const next = { ...prev }
        delete next[key]
        return next
      })
    },
    [nameTouched]
  )

  const handleCreate = useCallback(async () => {
    // Required-field gate: per-field red text + one toast naming the fields,
    // and the InputManager scrolls to and focuses the first bad field.
    const errors: Record<string, string> = {}
    fields.forEach((field) => {
      if (field.required && !isRequiredFieldValid((draft as unknown as Record<string, unknown>)[field.key])) {
        errors[field.key] = `${field.label || field.key} is required`
      }
    })
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors)
      setValidationErrorsVersion((v) => v + 1)
      showErrorToast(summariseFieldErrors([errors]) ?? 'Please complete the required fields')
      return
    }

    setSaving(true)
    try {
      const res = await createInvestorStatementRun(
        {
          name: String(draft[NAME] ?? '').trim(),
          period_end: dayjs(draft[PERIOD_END]).format(ISO_DATE),
          statement_date: dayjs(draft[STATEMENT_DATE]).format(ISO_DATE),
          notes: draft[NOTES]?.trim() ? draft[NOTES] : null
        },
        getAccessToken(store.getState().auth)
      )
      if (res.error || !res.data) {
        showErrorToast(res.error || 'Failed to create the statement run')
        return
      }
      const runId = res.data.run.investor_statement_runs__id
      toast.success('Statement run created')
      setDirty(false)
      // Morph the "new" tab into the run's tab in place (no remove+re-add,
      // which flashes the opener tab for one paint).
      dispatch(
        transformFormTab({
          key: formTabKey,
          module: TABLE_INVESTOR_STATEMENTS,
          recordId: runId,
          title: res.data.run.investor_statement_runs__name
        })
      )
      navigate(`/${TABLE_INVESTOR_STATEMENTS}/forms/full/${runId}`, { replace: true })
    } finally {
      setSaving(false)
    }
  }, [draft, fields, dispatch, navigate, formTabKey])

  return (
    <div className='h-full overflow-y-auto p-4'>
      <div className='flex items-center justify-between mb-3'>
        <Title level={4} className='!mb-0'>
          New Statement Run
        </Title>
        <div className='flex items-center gap-2'>
          {/* Fixed grammar: primary rightmost; Cancel only once something is typed. */}
          {dirty ? (
            <Button disabled={saving} onClick={() => closeFormTab(formTabKey)}>
              Cancel
            </Button>
          ) : null}
          <Button type='primary' loading={saving} onClick={() => void handleCreate()}>
            Create
          </Button>
        </div>
      </div>

      <Row gutter={[12, 12]}>
        <Col xs={24} xl={12}>
          <FormSection title='Details' titleColor='blue' scrollable={false}>
            <InputManager
              inputMode='edit'
              labelPosition='above'
              fields={fields}
              onChange={handleChange}
              validationErrors={validationErrors}
              validationErrorsVersion={validationErrorsVersion}
            />
          </FormSection>
        </Col>
      </Row>
    </div>
  )
}

// ---------------------------------------------------------------------------
// The run
// ---------------------------------------------------------------------------

function RunForm({ runId, formTabKey }: { runId: string; formTabKey: string }) {
  const dispatch = useDispatch()
  const { closeFormTab } = useTabManagerContext()
  const { run, loading, error, notFound, refetch, replace, generate, generating } = useInvestorStatementRun(runId)
  // "out of date": asked per fund once the run has loaded
  const { changes, recheck } = useRunFundChanges(run)
  // agent-written company descriptions the stored reports printed unreviewed
  const { warnings: descriptionWarnings, refetch: recheckDescriptions } = useRunDescriptionWarnings(run)
  const [deleting, setDeleting] = useState(false)
  // a fund's legal name or notice sender filled in on its own form shows
  // here when the user comes back to this tab - and so does a change to the
  // books, which puts stored statements out of date
  const refreshOnReturn = useCallback(async () => {
    await refetch()
    recheck()
    // a description signed off on the company form clears its warning
    void recheckDescriptions()
  }, [refetch, recheck, recheckDescriptions])
  const activateRef = useRefreshOnActivate(refreshOnReturn, { maxAgeMs: 0 })

  const name = run?.run.investor_statement_runs__name
  useEffect(() => {
    if (name) dispatch(updateTabTitle({ key: formTabKey, title: name }))
  }, [dispatch, formTabKey, name])

  const detailFields = useMemo(
    () =>
      run
        ? getRunFields(
            {
              [NAME]: run.run.investor_statement_runs__name,
              [PERIOD_END]: run.run.investor_statement_runs__period_end,
              [STATEMENT_DATE]: run.run.investor_statement_runs__statement_date,
              [NOTES]: run.run.investor_statement_runs__notes ?? null
            },
            {
              patchEndpoint: RUN_PATCH,
              periodLocked: run.run.prog__has_funds,
              statementDateLocked: run.run.prog__has_issued
            }
          )
        : [],
    [run]
  )

  const handleRunChange = useCallback((next: InvestorStatementRun) => replace(next), [replace])

  const handleDelete = useCallback(async () => {
    setDeleting(true)
    try {
      const res = await deleteInvestorStatementRun(runId, getAccessToken(store.getState().auth))
      if (!res.success) {
        showErrorToast(res.error || 'Failed to delete the statement run')
        return
      }
      if (res.filesNotRemoved > 0) {
        showErrorToast(`The run was deleted, but ${res.filesNotRemoved} of its files could not be moved to the trash.`)
      } else {
        toast.success('Statement run deleted')
      }
      closeFormTab(formTabKey)
    } finally {
      setDeleting(false)
    }
  }, [runId, closeFormTab, formTabKey])

  if (!run) {
    return loading ? (
      <div className='flex justify-center items-center h-64'>
        <Spin />
      </div>
    ) : (
      <div className='p-10 text-center text-gray-500'>
        {notFound ? 'Statement run not found.' : error || 'The statement run could not be loaded.'}
        <div className='mt-3'>
          <Button onClick={() => closeFormTab(formTabKey)}>Close</Button>
        </div>
      </div>
    )
  }

  // why the run cannot be deleted now; null when it can
  const deleteLocked = run.prog__is_active
    ? 'Statements of the run are being generated.'
    : run.run.prog__has_issued && !run.run.prog__is_app_admin
      ? 'The run holds statements that were issued: only an application administrator can delete it.'
      : null

  return (
    <div ref={activateRef} className='h-full overflow-y-auto p-4'>
      <div className='flex items-center justify-between mb-3'>
        <div className='flex items-center gap-3'>
          <Title level={4} className='!mb-0'>
            {run.run.investor_statement_runs__name}
          </Title>
          <Tag color='blue'>{run.run.prog__period_label}</Tag>
        </div>
        <div className='flex items-center gap-2'>
          {/* Fixed grammar: Delete (bordered danger) leftmost, Close rightmost. */}
          <Popconfirm
            title='Delete this statement run?'
            description={
              <div className='max-w-sm'>
                {run.prog__summary.statements > 0
                  ? 'The run, its funds and their stored statements are deleted; the files move to the trash.'
                  : 'The run and its funds are deleted.'}{' '}
                The funds can then join another run for {run.run.prog__period_label}.
              </div>
            }
            okText='Delete'
            okButtonProps={{ danger: true }}
            disabled={deleteLocked !== null}
            onConfirm={handleDelete}
          >
            <Tooltip title={deleteLocked ?? undefined}>
              <span>
                <Button danger loading={deleting} disabled={deleteLocked !== null}>
                  Delete
                </Button>
              </span>
            </Tooltip>
          </Popconfirm>
          <Button onClick={() => closeFormTab(formTabKey)}>Close</Button>
        </div>
      </div>

      <Row gutter={[12, 12]}>
        <Col xs={24} xl={12}>
          <FormSection title='Details' titleColor='blue' scrollable={false}>
            <InputManager
              inputMode='read'
              labelPosition='inline'
              enableAutoPatch
              patchId={runId}
              recordId={runId}
              currentRecord={run.run as Record<string, any>}
              fields={detailFields}
              onPatchSuccess={() => refetch()}
              fieldWrapping
            />
          </FormSection>
        </Col>
        <Col xs={24}>
          <RunFundsSection
            run={run}
            generate={generate}
            generating={generating}
            onRunChange={handleRunChange}
            refetch={refetch}
            changes={changes}
          />
        </Col>
        <Col xs={24}>
          <RunStatementsSection
            run={run}
            generate={generate}
            generating={generating}
            changes={changes}
            descriptionWarnings={descriptionWarnings.statements}
            refetch={refetch}
          />
        </Col>
        {run.run.prog__account_document_type ? (
          <Col xs={24}>
            <RunAccountDocumentsSection
              run={run}
              generate={generate}
              generating={generating}
              refetch={refetch}
              descriptionWarnings={descriptionWarnings.account_documents}
            />
          </Col>
        ) : null}
      </Row>
    </div>
  )
}
